import type { Action, Reducer, UnknownAction } from 'redux';
import { persistReducer } from 'redux-persist';
import type { PersistConfig } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

/**
 * redux-persist types whitelist and blacklist as `string[]`, so a name that isn't a key
 * of the slice compiles and then silently matches nothing. Narrowed to the slice's keys.
 */
export type PersistOptions<State> = Omit<
  Partial<PersistConfig<State>>,
  'whitelist' | 'blacklist'
> & {
  whitelist?: Extract<keyof State, string>[];
  blacklist?: Extract<keyof State, string>[];
};

/**
 * Wraps a reducer in redux-persist against localStorage. `opts` is spread last so a
 * caller can override the defaults.
 */
const persistFactory = <State, SliceAction extends Action = UnknownAction>(
  key: string,
  reducer: Reducer<State, SliceAction>,
  opts: PersistOptions<State> = {},
) => {
  const config: PersistConfig<State> = {
    key,
    storage,
    ...opts,
  };
  return persistReducer<State, SliceAction>(config, reducer);
};

export default persistFactory;
